import styled from '@emotion/styled'
import { concat, includes, without } from 'lodash'
import * as React from 'react'

import { useWhitelabel } from 'main/framework'
import { useProgramTypesForSearchFilter } from 'main/hooks/useProgramTypes'
import { breakpoints, Checkbox, Hidden, spacing, Strong, Text, Visible } from 'main/ui'
import { IFilters } from 'main/util/search'
import { useTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE } from '../../../../../../../common'
import { i18nKeyPrefix } from '../../../../../i18n'
import {
  Apply,
  CheckboxGroupContainer,
  Clear,
  FieldCopyContainer,
  FieldsContainer,
  FilterFormContainer as CommonFilterFormContainer,
  FilterFormSubmitContainer,
} from '../../common/FilterForm.styles'
import { ISearchFormProps } from '../../types'

const FilterFormContainer = styled(CommonFilterFormContainer)`
  @media (min-width: ${breakpoints.lg}px) {
    padding: ${spacing.majorScale(3)}px ${spacing.majorScale(4)}px ${spacing.majorScale(4)}px;
    max-width: 220px;
  }
`

const PRE = `${i18nKeyPrefix}-FILTERS-PROGRAM-TYPE-FILTER`

export default function FilterForm(props: ISearchFormProps) {
  const { filters, onChangeFilters, onRequestCloseForm } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const [params, setParams] = React.useState<IFilters>({ ...filters })

  return (
    <FilterFormContainer>
      <Fields params={params} onChangeParams={setParams} />
      <Visible lg xl>
        <FilterFormSubmitContainer css={{ marginTop: spacing.minorScale(5) }}>
          <Clear data-pendo="selectedProgramTypesFilter-clear" onClick={() => handleClear()}>
            {t(`${PRE}-CLEAR`)}
          </Clear>
          <Apply data-pendo="selectedProgramTypesFilter-apply" onClick={() => handleApply()}>
            {t(`${PRE}-APPLY`)}
          </Apply>
        </FilterFormSubmitContainer>
      </Visible>
    </FilterFormContainer>
  )

  function handleApply() {
    onRequestCloseForm()
    onChangeFilters({ ...filters, selectedProgramTypes: params.selectedProgramTypes })
  }

  function handleClear() {
    setParams({ ...params, selectedProgramTypes: [] })
  }
}

interface IFieldsProps {
  params: IFilters
  onChangeParams: (params: IFilters) => void
}

export function Fields(fieldsProps: IFieldsProps) {
  const { params, onChangeParams } = fieldsProps
  const formOptions = useProgramTypesForSearchFilter()
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  // Determine if the form needs a new label in whitelabeled search domains
  const filterCopy = chooseFormCopy()

  function chooseFormCopy() {
    const whitelabel = useWhitelabel()

    if (whitelabel.networkAbbr === 'NM') return `${PRE}-COPY-NM`
    return `${PRE}-COPY`
  }

  return (
    <div>
      <Visible lg xl>
        <Strong>{t(filterCopy)}</Strong>
      </Visible>
      <Hidden lg xl>
        <FieldCopyContainer>
          <Text>
            <Strong>{t(filterCopy)} -&nbsp;</Strong>
            {t(filterCopy)}
          </Text>
        </FieldCopyContainer>
      </Hidden>
      <FieldsContainer>
        <CheckboxGroupContainer css={{ width: 'max-content' }}>
          {formOptions.map(option => {
            const label = `${option.label}`
            return (
              <Checkbox
                data-pendo={`selectedProgramTypesFilter-${option.value}`}
                key={option.label}
                checked={includes(params.selectedProgramTypes, option.value)}
                label={label}
                onChange={(checked: boolean) => {
                  checked
                    ? onChangeParams({
                        ...params,
                        selectedProgramTypes: concat(params.selectedProgramTypes, option.value),
                      })
                    : onChangeParams({
                        ...params,
                        selectedProgramTypes: without(params.selectedProgramTypes, option.value),
                      })
                }}
              />
            )
          })}
        </CheckboxGroupContainer>
      </FieldsContainer>
    </div>
  )
}
